import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import FractionInput, { formatLatexPreview } from '../../../../components/inputs/FractionInput';

/* ── helpers ───────────────────────────────────────── */
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const powi = (a, k) => (k >= 0 ? Math.pow(a, k) : 1 / Math.pow(a, -k));

const gcd = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) [a, b] = [b, a % b]; return a || 1; };
function simplifyFrac(num, den) { if (den < 0) { num = -num; den = -den; } const g = gcd(num, den); return { num: num / g, den: den / g }; }
function toFraction(x, maxDen = 10000) {
  const sign = x < 0 ? -1 : 1; x = Math.abs(x);
  if (!isFinite(x)) return { num: sign, den: 0 };
  let h0=0,k0=1,h1=1,k1=0,y=x;
  for (let i=0;i<20;i++){ const a=Math.floor(y); const h2=a*h1+h0,k2=a*k1+k0; if(k2>maxDen)break;
    h0=h1;k0=k1;h1=h2;k1=k2; const frac=y-a; if(frac<1e-12)break; y=1/frac; }
  let num=h1,den=k1; if(k0!==0 && Math.abs(h0/k0-x)<Math.abs(num/den-x)){ num=h0; den=k0; }
  ({num,den}=simplifyFrac(num*sign,den)); return {num,den};
}
function niceLatexFromNumber(val) {
  if (!isFinite(val)) return String(val);
  const int = Math.round(val);
  if (Math.abs(val - int) < 1e-12) return String(int);
  let { num, den } = toFraction(val); ({ num, den } = simplifyFrac(num, den));
  const sgn = num < 0 ? '-' : ''; num = Math.abs(num);
  if (num > den) { const a = Math.floor(num/den), r = num - a*den; if (r === 0) return `${sgn}${a}`; return `${sgn}${a}\\;\\dfrac{${r}}{${den}}`; }
  return `${sgn}\\dfrac{${num}}{${den}}`;
}

/* subskrypt loga: „średni” + \tfrac */
function makeSubscriptLatex(latex){ return latex.replace(/\\dfrac/g,'\\tfrac').replace(/\\;/g,''); }
function logFromLatex(baseLatex){ return `\\log_{\\small ${makeSubscriptLatex(baseLatex)}}`; }

/* ułamki w wykładnikach kontra normalny rozmiar poza nimi */
function fracSup(latex){ return `\\scriptstyle ${latex.replace(/\\dfrac/g,'\\tfrac').replace(/\\;/g,'')}`; }
function fracNorm(latex){ return latex.replace(/\\dfrac/g,'\\tfrac').replace(/\\;/g,''); }

/* nawias przy potędze, jeśli baza to ułamek albo pierwiastek */
function parenIfNeeded(latex){ return /\\dfrac|\\frac|\\tfrac|\\sqrt|\//.test(latex) ? `\\left(${latex}\\right)` : latex; }

/* pierwiastek z KONKRETNEJ liczby (np. √32, ∛9) */
function asRootOfNumber(c, m, degree){
  const inner = powi(c, m); // 8, 32, 9, 27, 81, ...
  return degree === 2 ? `\\sqrt{${inner}}` : `\\sqrt[${degree}]{${inner}}`;
}

/* ── GENERATOR z wariantami ───────────────────────── */
export function generateTask(opts = {}) {
  const mode = opts.mode || 'mixed'; // 'integers' | 'fractions' | 'roots' | 'mixed'

  let pVal, qVal;            // wartości wykładników (liczby wymierne)
  let pLatex, qLatex;        // ich LaTeX (np. 3, -2, \dfrac{1}{3}, -\dfrac{5}{3})
  let AsDisplay, NsDisplay;  // treść (liczby/łamki/pierwiastki/potęgi)
  let baseSymbol = '2';      // 2 lub 3 (do wyjaśnień)
  let rootMeta = null;       // { c, degree, rootInBase, m, n } — tylko dla 'roots'

  function pickIntPos(){ return pick([1,2,3,4,5]); }
  function pickIntAny(){ return pick([-5,-4,-3,-2,-1,1,2,3,4,5]); }

  if (mode === 'integers') {
    // baza 2 LUB 3, obie potęgi dodatnie, w treści konkretne liczby; wynik niecałkowity
    const c = Math.random() < 0.5 ? 2 : 3;
    baseSymbol = String(c);
    const maxPow = c === 2 ? 5 : 4; // limity: 2→max 5, 3→max 4
    while (true) {
      const p = Math.floor(Math.random() * maxPow) + 1;
      const q = Math.floor(Math.random() * maxPow) + 1;
      if (q % p !== 0) { pVal = p; qVal = q; break; }
    }
    pLatex = `${pVal}`; qLatex = `${qVal}`;
    AsDisplay = String(powi(c, pVal));
    NsDisplay = String(powi(c, qVal));
  }
  else if (mode === 'fractions') {
    // co najmniej jeden ujemny wykładnik, wynik niecałkowity; treść jako liczby (np. 1/8)
    const c = 2; baseSymbol = '2';
    while(true){
      const p = pickIntAny(), q = pickIntAny();
      if (p === 0 || q === 0) continue;
      if (!(p < 0 || q < 0)) continue;
      if (Math.abs((q/p) - Math.round(q/p)) < 1e-12) continue;
      pVal = p; qVal = q; break;
    }
    pLatex = `${pVal}`; qLatex = `${qVal}`;
    AsDisplay = niceLatexFromNumber(powi(c, pVal));
    NsDisplay = niceLatexFromNumber(powi(c, qVal));
  }

  else if (mode === 'roots') {
    const c = Math.random() < 0.5 ? 2 : 3;
    baseSymbol = String(c);
    const maxPow = c === 2 ? 5 : 4;
    const degree = Math.random() < 0.5 ? 2 : 3;
    const rootInBase = Math.random() < 0.5;

    while (true) {
      const m = Math.floor(Math.random() * maxPow) + 1;
      const n = Math.floor(Math.random() * maxPow) + 1;

      if (rootInBase) {
        // sprawdzamy, czy pierwiastek nie daje całkowitej liczby
        const inner = powi(c, m);
        const rootVal = Math.pow(inner, 1/degree);
        if (Math.abs(rootVal - Math.round(rootVal)) < 1e-12) continue;

        pVal = m / degree;
        qVal = n;
        pLatex = `\\dfrac{${m}}{${degree}}`;
        qLatex = `${n}`;
        AsDisplay = asRootOfNumber(c, m, degree);
        NsDisplay = `${powi(c, n)}`;
      } else {
        if (m === n) continue;
        const inner = powi(c, n);
        const rootVal = Math.pow(inner, 1/degree);
        if (Math.abs(rootVal - Math.round(rootVal)) < 1e-12) continue;

        pVal = m;
        qVal = n / degree;
        pLatex = `${m}`;
        qLatex = `\\dfrac{${n}}{${degree}}`;
        AsDisplay = `${powi(c, m)}`;
        NsDisplay = asRootOfNumber(c, n, degree);
      }

      const val = qVal / pVal;
      if (Math.abs(val - Math.round(val)) > 1e-12) {
        rootMeta = { c, degree, rootInBase, m, n };
        break;
      }
    }
  }

  else { // 'mixed'
    const c = 2; baseSymbol = '2';
    while(true){
      const p = pickIntAny(), q = pickIntAny();
      if (p === 0 || q === 0) continue;
      if (Math.abs((q/p) - Math.round(q/p)) < 1e-12) continue;
      pVal = p; qVal = q; break;
    }
    pLatex = `${pVal}`; qLatex = `${qVal}`;
    AsDisplay = niceLatexFromNumber(powi(c,pVal));
    NsDisplay = niceLatexFromNumber(powi(c,qVal));
  }

  const value = qVal / pVal;

  // do wyjaśnień (potęgowo)
  const AsPow = `${baseSymbol}^{${pLatex}}`;
  const NsPow = `${baseSymbol}^{${qLatex}}`;

  return {
    type: 'LogComputePowerTask',
    data: {
      mode, pVal, qVal, pLatex, qLatex,
      AsDisplay, NsDisplay, AsPow, NsPow,
      value, baseSymbol,
      rootMeta
    }
  };
}

/* ── render ────────────────────────────────────────── */
export function renderTask({ data }) {
  const { AsDisplay, NsDisplay } = data;
  const expr = `\\,${logFromLatex(AsDisplay)} ${NsDisplay}`;

  return (
    <div style={{ marginBottom: 12 }}>
      Oblicz wartość logarytmu:{' '}
      <InlineMath math={expr} />
    </div>
  );
}


export function renderInput(task, userAnswer, onChange, disabled) {
  const handleChange = (ans) => {
    // bez _labelLatex – przekaż tylko odpowiedź
    onChange && onChange(ans);
  };
  return (
    <div className='space-y-2'>
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        label=""   // Podgląd: tylko wpisana liczba
      />
    </div>
  );
}

/* ── checking / answers ────────────────────────────── */
function coerceToNumber(userAnswer) {
  if (userAnswer == null) return null;
  if (typeof userAnswer === 'number' && isFinite(userAnswer)) return userAnswer;
  if (typeof userAnswer === 'string') {
    const n = Number(userAnswer.replace(',', '.'));
    return isFinite(n) ? n : null;
  }
  if (typeof userAnswer === 'object') {
    if (userAnswer.parsed != null && isFinite(userAnswer.parsed)) return Number(userAnswer.parsed);
    if (typeof userAnswer.value === 'number' && isFinite(userAnswer.value)) return userAnswer.value;
    const n = userAnswer.num ?? userAnswer.numerator ?? userAnswer.top;
    const d = userAnswer.den ?? userAnswer.denominator ?? userAnswer.bottom;
    if (n != null && d != null && Number(d) !== 0) {
      const v = Number(n) / Number(d);
      return isFinite(v) ? v : null;
    }
  }
  return null;
}

export function checkAnswer({ data }, userAnswer) {
  const v = coerceToNumber(userAnswer);
  if (v == null) return false;
  return Math.abs(v - data.value) < 1e-4;
}

/* ── user answer rendering (odporne na wszystko) ───── */
function extractAnswerLatex(u) {
  if (u == null) return null;
  if (typeof u === 'number' && isFinite(u)) return niceLatexFromNumber(u);
  if (typeof u === 'string') return formatLatexPreview(u);

  if (typeof u === 'object') {
    if (u.raw && String(u.raw).trim() !== '') return formatLatexPreview(u.raw);
    if (u.parsed != null && isFinite(u.parsed)) return niceLatexFromNumber(Number(u.parsed));
    if (typeof u.value === 'string' && u.value.trim() !== '') return formatLatexPreview(u.value);
    if (typeof u.text === 'string' && u.text.trim() !== '') return formatLatexPreview(u.text);
    const n = u.num ?? u.numerator ?? u.top;
    const d = u.den ?? u.denominator ?? u.bottom;
    if (n != null && d != null) return `\\dfrac{${n}}{${d}}`;
  }
  return null;
}

export function renderUserAnswer(...args) {
  // interesuje nas tylko sama odpowiedź, bez lewej strony logarytmu
  let userAnswer = null;

  if (args.length === 2) {
    const [, b] = args;
    userAnswer = b;
  } else if (args.length === 1) {
    userAnswer = args[0];
  }

  const ansLatex = extractAnswerLatex(userAnswer);
  if (!ansLatex) return <i>(brak odpowiedzi)</i>;

  return <InlineMath math={ansLatex} />;
}

export function renderAnswer({ data }) {
  // tylko wynik, bez logarytmu po lewej
  return <InlineMath math={niceLatexFromNumber(data.value)} />;
}

/* ── explanation ───────────────────────────────────── */
export function renderExplanation({ data }) {
  const { pLatex, qLatex, AsDisplay, NsDisplay, value, baseSymbol, rootMeta } = data;

  const basePow = parenIfNeeded(AsDisplay);

  const eqPowers = `${baseSymbol}^{\\left(${fracSup(pLatex)}\\right) x} = ${baseSymbol}^{${fracSup(qLatex)}}`;
  const eqExponents = `\\left(${fracNorm(pLatex)}\\right) x = ${fracNorm(qLatex)}`;
  const xRaw = `\\dfrac{${fracNorm(qLatex)}}{${fracNorm(pLatex)}}`.replace(/\\\\dfrac{([^}]*)}{1}/g, '$1');
  const xNice = niceLatexFromNumber(value);
  const showSimplifiedLine = xNice !== xRaw;

  let step2Blocks = [];
  if (rootMeta && rootMeta.rootInBase) {
    const left1 = AsDisplay;
    const middle1 = `\\left(${baseSymbol}^{${rootMeta.m}}\\right)^{${fracSup(`\\dfrac{1}{${rootMeta.degree}}`)}}`;
    const right1 = `${baseSymbol}^{${fracSup(pLatex)}}`;
    step2Blocks.push(`${left1} = ${middle1} = ${right1}`);
    step2Blocks.push(`${NsDisplay} = ${baseSymbol}^{${fracSup(qLatex)}}`);
  } else if (rootMeta && !rootMeta.rootInBase) {
    step2Blocks.push(`${AsDisplay} = ${baseSymbol}^{${fracSup(pLatex)}}`);
    const left2 = NsDisplay;
    const middle2 = `\\left(${baseSymbol}^{${rootMeta.n}}\\right)^{${fracSup(`\\dfrac{1}{${rootMeta.degree}}`)}}`;
    const right2 = `${baseSymbol}^{${fracSup(qLatex)}}`;
    step2Blocks.push(`${left2} = ${middle2} = ${right2}`);
  } else {
    step2Blocks.push(`${AsDisplay} = ${baseSymbol}^{${fracSup(pLatex)}},\\; ${NsDisplay} = ${baseSymbol}^{${fracSup(qLatex)}}`);
  }

  return (
    <div className='space-y-2'>
      <p><strong>Krok 1:</strong> Z definicji logarytmu:</p>
      <BlockMath math={`x = ${logFromLatex(AsDisplay)} ${NsDisplay} \\;\\iff\\; ${basePow}^{\\,x} = ${NsDisplay}`} />

      <p><strong>Krok 2:</strong> Zapisujemy podstawę i argument jako potęgi liczby {baseSymbol}:</p>
      {step2Blocks.map((m, i) => <BlockMath key={i} math={m} />)}

      <p><strong>Krok 3:</strong> Rozwiązujemy równanie wykładnicze:</p>
      <BlockMath math={`${basePow}^{\\,x} = ${NsDisplay} \\;\\iff\\; ${eqPowers}`} />
      <BlockMath math={`${eqExponents} \\;\\iff\\; x = ${xRaw}`} />
      {showSimplifiedLine && <BlockMath math={`x = ${xNice}`} />}

      <p><strong>Wniosek:</strong></p>
      <BlockMath math={`${logFromLatex(AsDisplay)} ${NsDisplay} = ${xNice}`} />
    </div>
  );
}
